import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Eye, EyeOff, UserPlus, Coffee, Users, Heart } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { CreatorSetup } from '../components/CreatorSetup';
import { getApiUrl } from '../config/config';

const SignupContainer = styled.div`
  min-height: 100vh; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%); padding: 20px;
`;

const SignupCard = styled(motion.div)`
  background: #111111; border: 1px solid #333333; border-radius: 20px; padding: 40px; width: 100%; max-width: 450px; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  @media (max-width: 480px) { padding: 30px 20px; border-radius: 16px; }
`;

const Logo = styled.div`
  text-align: center; margin-bottom: 32px;
  h1 { background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; font-size: 2rem; font-weight: 700; font-family: var(--font-heading); margin-bottom: 8px; @media (max-width: 480px){ font-size: 1.75rem; } }
  p { color: #cccccc; font-size: 0.875rem; font-family: var(--font-body); }
`;

const Form = styled.form`display: flex; flex-direction: column; gap: 20px;`;
const FormGroup = styled.div`display: flex; flex-direction: column; gap: 8px;`;
const Label = styled.label`color: #ffffff; font-weight: 500; font-size: 0.875rem; font-family: var(--font-body);`;

const Input = styled.input`
  padding: 12px 16px; border: 2px solid #333333; border-radius: 8px; background: #111111; color: #ffffff; font-size: 16px; font-family: var(--font-body); transition: border-color 0.3s ease; width: 100%; box-sizing: border-box;
  &:focus { outline: none; border-color: #e0c16f; box-shadow: 0 0 0 3px rgba(224, 193, 111, 0.1); }
  &::placeholder { color: #666666; }
  &:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px #111111 inset !important; -webkit-text-fill-color: #ffffff !important; transition: background-color 5000s ease-in-out 0s;
  }
`;

const PasswordInput = styled.div`position: relative; input { padding-right: 48px; }`;
const PasswordToggle = styled.button`position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #cccccc; cursor: pointer; padding: 4px; &:hover { color: #ffffff; }`;

const RoleSelector = styled.div`
  display: flex; gap: 12px; margin-top: 8px;
  /* Na telefonu ređamo opcije jednu ispod druge da bi se lakše kliknulo */
  @media (max-width: 480px) { flex-direction: column; }
`;

const RoleOption = styled.label`
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 16px; border: 2px solid #333333; border-radius: 8px; cursor: pointer; background: transparent; transition: all 0.3s ease;
  input[type="radio"] { display: none; }
  &:hover { border-color: #e0c16f; }
  input[type="radio"]:checked + div { border-color: #e0c16f; background: rgba(224, 193, 111, 0.1); }
`;

const RoleContent = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 16px; border: 2px solid transparent; border-radius: 8px; transition: all 0.3s ease; width: 100%; box-sizing: border-box;
  svg { color: #e0c16f; width: 24px; height: 24px; }
  span { font-weight: 500; color: #ffffff; }
  small { color: #cccccc; text-align: center; font-size: 0.75rem; }
`;

const SubmitButton = styled.button`
  padding: 16px 24px; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000000; border: none; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; gap: 8px;
  @media (max-width: 480px) { padding: 14px 20px; font-size: 15px; }
  &:hover { background: linear-gradient(95.36deg, #bd851b 5.18%, #e0c16f 93.21%); transform: translateY(-2px); box-shadow: 0 4px 15px rgba(224, 193, 111, 0.3); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const Divider = styled.div`text-align: center; margin: 24px 0; position: relative; &::before { content: ''; position: absolute; top: 50%; left: 0; right: 0; height: 1px; background: #333333; } span { background: #111111; padding: 0 16px; color: #cccccc; font-size: 0.875rem; position: relative; z-index: 1; }`;
const LoginLink = styled.div`text-align: center; color: #cccccc; font-size: 0.875rem; a { background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; text-decoration: none; font-weight: 500; &:hover { text-decoration: underline; } }`;
const ErrorMessage = styled.div`color: #ff6b6b; font-size: 0.875rem; text-align: center;`;

const SignupPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreatorSetup, setShowCreatorSetup] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register: registerUser } = useAuth();
  
  const { register, handleSubmit, formState: { errors } } = useForm({defaultValues:{username:searchParams.get('username') || ''}});

  const onSubmit = async (data) => {
    setIsLoading(true); setError('');
    try {
      const result = await registerUser(data);
      if (result.success) {
        if (data.role === 'creator') {
          setShowCreatorSetup(true);
        } else {
          toast.success('Account created successfully!');
          navigate('/dashboard');
        }
      } else {
        setError(result.error); toast.error(result.error);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred'); toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const createCreatorProfile = async (setupData = null) => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    const setupResponse = await fetch(`${getApiUrl()}/creator-content/setup-creator`, { method: 'POST', headers });
    const setupResult = await setupResponse.json();
    if (!setupResponse.ok) throw new Error(setupResult.error || 'Unable to create creator profile');

    if (setupData) {
      const profileResponse = await fetch(`${getApiUrl()}/users/profile`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: setupData.displayName || undefined,
          bio: setupData.bio || '',
          tags: setupData.interests || []
        })
      });
      const profileResult = await profileResponse.json();
      if (!profileResponse.ok) throw new Error(profileResult.error || 'Unable to save creator profile');

      if (setupData.profilePicture) {
        const imageBlob = await fetch(setupData.profilePicture).then(response => response.blob());
        const extension = imageBlob.type.split('/')[1] || 'jpg';
        const avatarData = new FormData();
        avatarData.append('avatar', imageBlob, `avatar.${extension}`);
        const avatarResponse = await fetch(`${getApiUrl()}/users/profile/avatar`, {
          method: 'POST', headers, body: avatarData
        });
        const avatarResult = await avatarResponse.json();
        if (!avatarResponse.ok) throw new Error(avatarResult.error || 'Unable to upload avatar');
      }
    }
  };

  const handleCreatorSetupComplete = async (setupData) => {
    try {
      await createCreatorProfile(setupData);
      toast.success('Creator profile setup completed!');
      navigate('/dashboard');
    } catch (setupError) {
      toast.error(setupError.message);
    }
  };
  const handleCreatorSetupSkip = async () => {
    try {
      await createCreatorProfile();
      toast.success('Account created successfully! You can complete your profile later.');
      navigate('/dashboard');
    } catch (setupError) {
      toast.error(setupError.message);
    }
  };
  const handleCreatorSetupEdit = () => {};

  if (showCreatorSetup) return <CreatorSetup onComplete={handleCreatorSetupComplete} onSkip={handleCreatorSetupSkip} onEdit={handleCreatorSetupEdit} />;

  return (
    <SignupContainer>
      <SignupCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Logo>
          <h1><Coffee size={32} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> BUCX</h1>
          <p>Join the creator revolution</p>
        </Logo>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="username">Username</Label>
            <Input id="username" type="text" placeholder="Choose a username" {...register('username', { required: 'Username is required', minLength: { value: 3, message: 'Username must be at least 3 characters' }, maxLength: { value: 20, message: 'Username must be less than 20 characters' } })} />
            {errors.username && <ErrorMessage>{errors.username.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" placeholder="Enter your email" {...register('email', { required: 'Email is required', pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' } })} />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <PasswordInput>
              <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Create a password" {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })} />
              <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </PasswordToggle>
            </PasswordInput>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>I want to be a...</Label>
            <RoleSelector>
              <RoleOption>
                <input type="radio" value="supporter" {...register('role', { required: 'Please select a role' })} />
                <RoleContent>
                  <Heart /> <span>Supporter</span>
                  <small>Support creators with donations and memberships</small>
                </RoleContent>
              </RoleOption>
              
              <RoleOption>
                <input type="radio" value="creator" {...register('role', { required: 'Please select a role' })} />
                <RoleContent>
                  <Users /> <span>Creator</span>
                  <small>Receive support and build your community</small>
                </RoleContent>
              </RoleOption>
            </RoleSelector>
            {errors.role && <ErrorMessage>{errors.role.message}</ErrorMessage>}
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Creating account...' : <><UserPlus size={20} /> Create Account</>}
          </SubmitButton>
        </Form>

        <Divider><span>or</span></Divider>
        <LoginLink>Already have an account?{' '}<Link to="/login">Sign in here</Link></LoginLink>
      </SignupCard>
    </SignupContainer>
  );
};

export default SignupPage;
